import { useParams } from "react-router-dom";
import { COMMENT_MODE, COMMENT_TYPE, CustomFormProps } from "context/CustomFormContext";
import { useStores } from "stores";

import { ApiKeyFields, CommentForm, PrimaryFormNames, ReleaseStatus } from "types";

import { ReleaseUrlParams } from "../types";

export type ReturnReleaseFormState = CustomFormProps & {
  isReadOnly: boolean; // Indicates whether the form is read-only
  isEditable: boolean; // Indicates whether the form is editable
};

/**
 * Hook to manage the state of the primary form level.
 *
 * This hook handles access permissions, comment-related operations,
 * and form fields' read/edit status based on the release's status
 * and the user's role (admin or regular user).
 *
 * @returns {ReturnReleaseFormState} Object representing the form's state,
 * including read-only, editable fields, and comment configurations.
 */
export const usePrimaryFormState = (): ReturnReleaseFormState => {
  const {
    revisedFormStore: {
      commentsLvl1FieldKeys,
      comments: { postPrimaryComment, patchPrimaryComment, deletePrimaryComment, primaryComments },
      release,
    },
    userStore: { isAdmin }, // Whether the current user is an admin
  } = useStores();

  // Extract user and release identifiers from URL parameters
  const { userId = null, releaseId } = useParams<ReleaseUrlParams>();

  /**
   * Callback function to handle comment operations.
   *
   * @param {CommentForm} comment - The comment object to be added, edited, or deleted.
   * @param {COMMENT_MODE} mode - Operation mode: ADD, EDIT, or DELETE.
   */
  const commentCallback = async (comment: CommentForm, mode: COMMENT_MODE) => {
    if (!releaseId) return; // Ensure a release ID is available

    switch (mode) {
      case COMMENT_MODE.ADD:
        postPrimaryComment(userId, PrimaryFormNames.Releases, releaseId, comment);
        return;
      case COMMENT_MODE.EDIT:
        patchPrimaryComment(userId, PrimaryFormNames.Releases, releaseId, comment.fieldName, comment);
        return;
      case COMMENT_MODE.DELETE:
        deletePrimaryComment(userId, PrimaryFormNames.Releases, releaseId, comment.fieldName);
        return;
    }
  };

  // Handle form state if the release is already "Released"
  if (release?.status === ReleaseStatus.Released) {
    return {
      isReadOnly: true,
      readOnly: { fields: true },
      edit: { fields: [] },
      comments: { fields: [] },
      isAdmin,
      isEditable: false,
    };
  }

  // Handle form state for administrators
  if (isAdmin) {
    switch (release?.status) {
      case ReleaseStatus.Draft:
        return {
          isReadOnly: true,
          readOnly: { fields: true },
          edit: { fields: [] },
          comments: { fields: [] },
          isAdmin,
          isEditable: false,
        };
      case ReleaseStatus.InRevision:
        return {
          isReadOnly: true,
          readOnly: { fields: true },
          edit: {
            fields: true,
            excludeFields: [ApiKeyFields.cover_art_blob_path, ApiKeyFields.ai_cover_art, ApiKeyFields.date_confirmation],
          },
          comments: {
            fields: true,
            excludeFields: [ApiKeyFields.schedule_earlier, ApiKeyFields.date_confirmation],
            commentCallback,
            formComments: primaryComments,
          },
          isAdmin,
          isEditable: true,
        };
      case ReleaseStatus.ChangesNeeded:
        return {
          isReadOnly: true,
          readOnly: { fields: true },
          edit: { fields: false },
          comments: {
            fields: commentsLvl1FieldKeys,
            formComments: primaryComments,
            commentTypes: COMMENT_TYPE.READ_ONLY,
          },
          isAdmin,
          isEditable: false,
        };
    }
  }

  // Handle form state for non-admin users
  switch (release?.status) {
    case ReleaseStatus.Draft:
      return {
        isReadOnly: false,
        readOnly: { fields: false },
        edit: { fields: false },
        comments: { fields: false },
        isEditable: false,
      };
    case ReleaseStatus.InRevision:
      return {
        isReadOnly: true,
        readOnly: { fields: true },
        edit: { fields: false },
        comments: { fields: false },
        isEditable: false,
      };
    case ReleaseStatus.ChangesNeeded:
      return {
        isReadOnly: false,
        readOnly: {
          fields: true,
          excludeFields: [...commentsLvl1FieldKeys, ApiKeyFields.schedules_at],
        },
        edit: { fields: false },
        comments: {
          fields: commentsLvl1FieldKeys,
          formComments: primaryComments,
          commentCallback,
          commentTypes: COMMENT_TYPE.CLIENT,
        },
        isEditable: true,
      };
  }

  // Default fallback if no status matches
  return {
    isReadOnly: true,
    readOnly: { fields: false },
    edit: { fields: false },
    comments: { fields: false },
    isEditable: false,
  };
};
